/**
 * Mirrors the backend enum. Roles are hierarchical: super-admin > admin > user.
 * Ask `isAdminRole()` from `@/utils/roles` rather than comparing against `admin`,
 * or super admins fall through to the employee UI.
 */
export enum EmployeeRoles {
  superAdmin = "super-admin",
  admin = "admin",
  user = "user"
}

/** Product modules the platform admin can enable per organization. */
export enum PlatformModule {
  attendance = "attendance",
  leave = "leave",
  payroll = "payroll",
  projectManagement = "project-management",
  recruitment = "recruitment",
  chat = "chat",
  files = "files",
}

export enum EmployeeTitles {
  mr = "Mr.",
  mrs = "Mrs.",
  miss = "Miss",
  ms = "Ms.",
  dr = "Dr.",
  prof = "Prof.",
  eng = "Eng.",
  arch = "Arch.",
}

export enum EducationLevel {
  highSchoolDiploma = "Diploma",
  bachelorsDegree = "Bachelors Degree",
  mastersDegree = "Masters Degree",
  doctorateDegree = "Doctorate Degree",
  postDoctorateDegree = "Post Doctorate Degree",
}

export enum ContractTypes {
  fullTime = "full-time",
  partTime = "part-time",
  contractual = "contractual",
  internship = "internship",
}

export enum PayrollStatus {
  pending = "pending",
  generated = "generated",
  approved = "approved"
}

export enum PayrollComponentType {
  addition = "addition",
  deduction = "deduction"
}

export enum PayrollComponentCalculationType {
  fixed = "fixed",
  percent = "percent",
  custom = "custom"
}

export enum PayrollComponentBaseField {
  basic = "basic",
  gross = "gross",
  taxable = "taxable",
}

export enum PayrollComponentDeductFrom {
  grossSalary = "grossSalary",
  basicSalary = "basicSalary",
}

export enum OvertimeDayTypes {
  monday = "monday",
  tuesday = "tuesday",
  wednesday = "wednesday",
  thursday = "thursday",
  friday = "friday",
  saturday = "saturday",
  sunday = "sunday",
  holiday = "holiday",
}

export enum AttendanceLogType {
  checkIn = "check-in",
  checkOut = "check-out",
}

export enum AttendanceLogInputType {
  application = "application",
  fingerprint = "fingerprint",
}

export enum AttendanceLoggedByType {
  user = "user",
  admin = "admin",
}

export enum AttendanceFilters {
  today = "today",
  yesterday = "yesterday",
  thisWeek = "thisWeek",
  thisMonth = "thisMonth",
}

export enum LeaveGenderEligibility  {
  male = "male",
  female = "female",
  any = "any",
}

export enum LeaveAccrualMethod {
  none = "none",
  monthly = "monthly",
  annual = "annual",
}

export enum LeaveProrationMethod  {
  daily = "daily",
  monthly = "monthly",
}

export enum MaritalStatuses  {
  single = "single",
  married = "married",
  divorced = "divorced"
}

export enum LeaveUnit  {
  day = "day",
  halfDay = "halfDay",
}

export enum LeaveHalfDaySlots  {
  am = "am",
  pm = "pm"
}

export enum ProjectStatus {
  active = "active",
  planning = "planning",
  inProgress = "in-progress",
  onHold = "on-hold",
  completed = "completed",
  cancelled = "cancelled",
}

export enum ProjectTransactionType {
  earning = "earning",
  expense = "expense",
}

export enum ProjectTransactionCategory {
  invoice = "invoice",
  retainer = "retainer",
  milestone = "milestone",
  timesheetRevenue = "timesheet-revenue",
  expenseReimburse = "expense-reimburse",
  payrollCost = "payroll-cost",
  adjustment = "adjustment",
}

export enum ProjectTransactionSourceType {
  invoice = "invoice",
  expense = "expense",
  payroll = "payroll",
  manual = "manual",
}

export enum Currency {
  etb = "etb",
  usd = "usd",
  eur = "eur",
}

export enum ProjectEmployeeRole {
  manager = "manager",
  lead = "lead",
  member = "member",
  contractor = "contractor",
}

export enum MilestoneStatus {
  pending = "pending",
  inProgress = "in-progress",
  completed = "completed",
  cancelled = "cancelled",
}

export enum InvoiceStatus {
  draft = "draft",
  sent = "sent",
  paid = "paid",
  overdue = "overdue",
  cancelled = "cancelled",
}

export enum LeaveLedgerEntryType {
  accrual = "accrual",
  adjustment = "adjustment",
  opening = "opening",
  taken = "taken",
  encashment = "encashment",
  expiry = "expiry",
}

export enum JobPostCustomFieldType {
  text = "text",
  date = "date",
  file = "file",
}

export enum JobApplicationStatus  {
  pending = "pending",
  interview = "interview",
  rejected = "rejected",
  accepted = "accepted",
}

export enum EmployeeOffboardingType {
  resignation = "resignation",
  termination = "termination",
  contractEnd = "contract_end",
  retirement = "retirement",
  death = "death",
  other = "other",
}

export enum EmployeeOffboardingStatus {
  pending = "pending",
  inProgress = "in_progress",
  completed = "completed",
  cancelled = "cancelled",
}

export enum KnowledgeBaseStatus {
  draft = "draft",
  published = "published",
  archived = "archived",
}

export enum CalendarSystem {
  GREGORIAN = "GREGORIAN",
  ETHIOPIC = "ETHIOPIC",
}